<template>
  <div class="clients-view">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ t('clients.title') }}</h1>
        <p class="page-sub">{{ t('clients.sub') }}</p>
      </div>
      <button class="btn btn-primary" @click="openForm(null)">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        {{ t('clients.addClient') }}
      </button>
    </div>

    <div class="clients-search">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      <input v-model="query" class="form-control" :placeholder="t('clients.searchPh')" />
    </div>

    <div v-if="loading" class="spinner"></div>

    <div v-else class="clients-table-wrap">
      <table class="clients-table">
        <thead>
          <tr>
            <th>{{ t('clients.name') }}</th>
            <th>{{ t('clients.username') }}</th>
            <th>{{ t('clients.projects') }}</th>
            <th>{{ t('clients.status') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in filteredClients" :key="c.id">
            <td>
              <div class="member-name">
                <div class="member-avatar">{{ initials(c.name) }}</div>
                {{ c.name }}
              </div>
            </td>
            <td class="username-cell" :data-label="t('clients.username')">{{ c.username }}</td>
            <td :data-label="t('clients.projects')">
              <span v-if="c.projects && c.projects.length" class="proj-tags">
                <span v-for="(p, i) in c.projects" :key="i" class="proj-tag">{{ p }}</span>
              </span>
              <span v-else class="proj-none">—</span>
            </td>
            <td :data-label="t('clients.status')">
              <span :class="['status-dot', c.is_active ? 'active' : 'inactive']">
                {{ c.is_active ? t('clients.active') : t('clients.inactive') }}
              </span>
            </td>
            <td>
              <div v-if="isManager" class="row-actions">
                <button class="btn btn-sm btn-ghost" @click="openForm(c)">{{ t('common.edit') }}</button>
                <button
                  class="btn btn-sm btn-ghost"
                  :class="{ 'danger-text': c.is_active }"
                  @click="toggleActive(c)"
                >{{ c.is_active ? t('clients.deactivate') : t('clients.activate') }}</button>
              </div>
            </td>
          </tr>
          <tr v-if="filteredClients.length === 0">
            <td colspan="5" class="empty-cell">{{ t('clients.empty') }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add / Edit modal -->
    <div v-if="showForm" class="modal-backdrop" @click.self="showForm = false">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ editing ? t('clients.editClient') : t('clients.addClient') }}</h2>
          <button class="btn btn-icon modal-close" @click="showForm = false">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <form @submit.prevent="submitForm">
          <div class="form-group">
            <label>{{ t('clients.fullName') }} *</label>
            <input v-model="form.name" class="form-control" required maxlength="120" />
          </div>
          <div class="form-group">
            <label>{{ t('clients.username') }} *</label>
            <input
              v-model="form.username"
              class="form-control"
              required
              maxlength="60"
              :disabled="!!editing"
              :style="editing ? 'opacity:.5;pointer-events:none' : ''"
              :placeholder="t('clients.usernamePlaceholder')"
            />
          </div>
          <div class="form-group">
            <label>{{ editing ? t('clients.newPassword') : t('clients.passwordLabel') + ' *' }}</label>
            <input
              v-model="form.password"
              type="password"
              class="form-control"
              :required="!editing"
              :placeholder="t('clients.passwordPlaceholder')"
            />
          </div>
          <p v-if="formError" class="form-error">{{ formError }}</p>
          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" @click="showForm = false">{{ t('common.cancel') }}</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? t('common.saving') : (editing ? t('common.save') : t('clients.addClient')) }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'
import { useAuth } from '@/composables/useAuth'

export default {
  name: 'ClientsView',
  setup() {
    const { t }     = useI18n()
    const { isManager: _isManager } = useAuth()
    const isManager = computed(() => _isManager())
    const clients   = ref([])
    const query     = ref('')
    const loading   = ref(true)

    // Filter by client name, username, or any affiliated project name.
    const filteredClients = computed(() => {
      const term = query.value.trim().toLowerCase()
      if (!term) return clients.value
      return clients.value.filter(c =>
        (c.name || '').toLowerCase().includes(term) ||
        (c.username || '').toLowerCase().includes(term) ||
        (c.projects || []).some(p => (p || '').toLowerCase().includes(term))
      )
    })
    const showForm  = ref(false)
    const editing   = ref(null)
    const saving    = ref(false)
    const formError = ref('')

    const form = reactive({ name: '', username: '', password: '' })

    async function load() {
      loading.value = true
      try { clients.value = (await api.getClients()).data || [] } catch {} finally { loading.value = false }
    }

    function openForm(client) {
      editing.value = client
      Object.assign(form, { name: client?.name || '', username: client?.username || '', password: '' })
      formError.value = ''
      showForm.value  = true
    }

    async function submitForm() {
      formError.value = ''
      saving.value    = true
      try {
        if (editing.value) {
          const payload = { name: form.name }
          if (form.password) payload.password = form.password
          await api.updateClient(editing.value.id, payload)
        } else {
          await api.createClient({ name: form.name, username: form.username, password: form.password })
        }
        showForm.value = false
        await load()
      } catch (e) {
        // Surface the specific field errors from a 422 instead of the generic message.
        const errs = e.data && e.data.errors
        formError.value = errs
          ? Object.values(errs).flat().join(' ')
          : (e.message || t('taskForm.saveFailed'))
      } finally {
        saving.value = false
      }
    }

    async function toggleActive(client) {
      const verb = client.is_active ? t('clients.deactivate') : t('clients.activate')
      if (!confirm(t('clients.toggleConfirm', { verb, name: client.name }))) return
      try { await api.updateClient(client.id, { is_active: !client.is_active }); await load() } catch {}
    }

    const initials = (name) => name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

    onMounted(load)
    return { t, isManager, clients, filteredClients, query, loading, showForm, editing, form, saving, formError, openForm, submitForm, toggleActive, initials }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }

.clients-search {
  display: flex; align-items: center; gap: 8px;
  max-width: 360px; margin-bottom: 16px;
  padding: 0 12px; border: 1px solid var(--border); border-radius: var(--radius-sm, 8px);
  background: var(--surface); color: var(--muted);
}
.clients-search input {
  border: none; background: none; padding: 9px 0; flex: 1; color: var(--text); font: inherit;
}
.clients-search input:focus { outline: none; }

.proj-tags { display: flex; flex-wrap: wrap; gap: 5px; }
.proj-tag {
  font-size: 11px; padding: 2px 8px; border-radius: 999px;
  background: var(--accent-wash, var(--surface-2)); color: var(--text);
  border: 1px solid var(--border);
}
.proj-none { color: var(--faint, var(--muted)); }

.clients-table-wrap {
  background: var(--surface);
  border: 1.5px solid var(--card-brd);
  border-radius: var(--radius);
  overflow: auto;
}
.clients-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.clients-table th {
  text-align: left;
  padding: 12px 16px;
  color: var(--muted);
  font-weight: 500;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .05em;
  border-bottom: 1px solid var(--border);
}
.clients-table td {
  padding: 14px 16px;
  border-bottom: 1px solid var(--border);
}
.clients-table tr:last-child td { border-bottom: none; }

.member-name { display: flex; align-items: center; gap: 10px; font-weight: 500; }
.member-avatar {
  width: 30px; height: 30px;
  border-radius: 50%;
  background: var(--primary);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.username-cell { color: var(--muted); font-family: monospace; }

.status-dot { font-size: 12px; }
.status-dot.active   { color: var(--success); }
.status-dot.inactive { color: var(--muted); }

.row-actions { display: flex; gap: 6px; }
.danger-text { color: var(--danger); }
.empty-cell  { text-align: center; color: var(--muted); padding: 32px !important; }

.modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.6);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 20px;
}
.modal {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  width: 100%; max-width: 440px;
  padding: 28px;
  box-shadow: var(--shadow);
}
.modal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.modal-header h2 { font-size: 18px; font-weight: 700; }
.modal-close { color: var(--muted); }
.form-error {
  color: var(--danger); font-size: 13px; margin-bottom: 12px;
  padding: 8px 12px; background: rgba(239,68,68,.1); border-radius: 6px;
}
.modal-actions {
  display: flex; justify-content: flex-end; gap: 10px;
  margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--border);
}

/* Inline cell labels — hidden on desktop, shown when the table stacks into cards. */
.clients-table td[data-label]::before {
  content: attr(data-label);
  display: none;
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: .05em;
  margin-inline-end: 8px;
}

/* Mobile: the table reflows into stacked cards (matches Tasks/Projects at 768px). */
@media (max-width: 768px) {
  .clients-table-wrap {
    border: none;
    background: transparent;
    overflow: visible;
  }
  .clients-table { font-size: 14px; }
  .clients-table thead { display: none; }
  .clients-table tbody,
  .clients-table tr,
  .clients-table td { display: block; width: 100%; }

  .clients-table tr {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 12px 14px;
    margin-bottom: 10px;
  }
  .clients-table td {
    padding: 5px 0;
    border-bottom: none;
  }
  .clients-table td[data-label]::before { display: inline; }

  .member-name { margin-bottom: 4px; }
  .row-actions { margin-top: 8px; }
  .empty-cell { padding: 24px 0 !important; }
}
</style>
